export default class SplashScreen extends Phaser.Scene {
  /**
   *  Takes care of loading the main game assets, including textures, tile
   *  maps, sound effects and other binary files, while displaying a busy
   *  splash screen.
   *
   *  @extends Phaser.Scene
   */
  constructor() {
    super({
      key: 'SplashScreen',

      //  Splash screen and progress bar textures.
      pack: {
        files: [
          {
            key: 'splash-screen',
            type: 'image'
          },
          {
            key: 'progress-bar',
            type: 'image'
          },
          {
            key: 'progress-bg',
            type: 'image'
          }
        ]
      }
    });
  }

  /**
   *  Show the splash screen and prepare to load game assets.
   *
   *  @protected
   */
  preload() {
    //  Display cover and progress bar textures.
    this.showCover();
    this.setupRotationHandling();
    this.setupiOS8BrowserCompatability();
    let dataPath = 'gameData.json';
    this.load.json('data', dataPath);
    this.load.json('localisation','localisation.json');
    this.load.json('config','../config.json');

    this.load.bitmapFont(
      'HelloHeadline',
      'fonts/helloheadline.png',
      'fonts/helloheadline.xml'
    );
  }


  setupRotationHandling() {
    this.createRotateMessage();
    this.startedInPortrait = (this.scene.scene.scale.orientation === Phaser.Scale.PORTRAIT);
    if (!this.startedInPortrait) {
      this.hideOrientationMessage();
    }
    this.scene.scene.scale.refresh();

    let that = this;
    this.scene.scene.scale.on('orientationchange', function (orientation) {
      if (orientation === Phaser.Scale.PORTRAIT) {
        that.showOrientationMessage();
      } else if (orientation === Phaser.Scale.LANDSCAPE) {
        that.hideOrientationMessage();
      }
    });
  }

  setupiOS8BrowserCompatability() {
    if (this.scene.systems.game.device.os.iOS) {
      if (this.scene.systems.game.device.os.iOSVersion === 8) {
        let that = this;
        this.scene.scene.scale.on('resize', function (gameSize, baseSize, displaySize, resolution) {
          that.gameResized();
        });

        this.makeFullScreenMessage();
        this.makeFullScreenMessageBG();
        this.gameResized();
      }
      else {
        this.scene.scene.scale.refresh();

      }
    }
  }

  /**
   *  Set up animations, plugins etc. that depend on the game assets we just
   *  loaded.
   *
   *  @protected
   */
  create() {
    this.showProgressBar();
    this.scene.scene.scale.refresh();
    //  HINT: Declare all game assets to be loaded here.
    this.load.image('logo');
    this.load.image('startBtn', 'play_btn_lrg.png');
    this.load.image('playBtn', 'play_btn_small.png');
    this.load.image('bandit', 'bandit_static.png');
    this.load.image('bingo', 'bingo_static.png');
    // this.load.image('resetBtn');
    this.load.image('score_box');
    this.load.image('score_popup');
    this.load.image('blank');
    this.load.image('hand', 'help_hand.png');
    this.load.image('titleBG', 'splash-screen.png');
    this.load.image('pauseBtn', 'pause_btn.png');
    this.load.image('pause_popup');
    this.load.image('popup');
    this.load.image('sound_on', 'sound_btn_on.png');
    this.load.image('sound_off', 'sound_btn_off.png');
    this.load.image('helpBtn', 'help_btn.png');

    this.gameConfig = this.cache.json.get('data').gameConfig;
    this.spineData = this.cache.json.get('data').spineData;
    this.sounds = this.cache.json.get('data').soundConfig;
    this.idleSFX = this.cache.json.get('data').idleSFX;
    this.helpConfig = this.cache.json.get('data').helpConfig;

    window.language = this.cache.json.get('config').language;

    this.load.atlas('sprites', 'spritesheets/keepyuppy_sprites.png', 'spritesheets/keepyuppy_sprites.json');

    for (let p = 0; p < this.helpConfig.steps.length; p++) {
      this.load.image(this.helpConfig.steps[p].image);
    }

    let idleSFXLength = this.idleSFX.length;
    for (let p = 0; p < idleSFXLength; p++) {
      let cSound = this.idleSFX[p];
      let oggPath = 'sound/' + cSound + '.ogg';
      let mp3Path = 'sound/' + cSound + '.mp3';
      this.load.audio(cSound, [oggPath, mp3Path]);
    }

    let soundsLength = this.sounds.length;
    for (let m = 0; m < soundsLength; m++) {
      let cSound = this.sounds[m];
      if (cSound.variants !== undefined) {
        for (let n = 0; n < cSound.variants; n++) {
          let oggPath = 'sound/' + cSound.file + (n + 1) + '.ogg';
          let mp3Path = 'sound/' + cSound.file + +(n + 1) + '.mp3';
          this.load.audio(cSound.name + (n + 1), [oggPath, mp3Path]);
        }
      } else {
        let oggPath = 'sound/' + cSound.file + '.ogg';
        let mp3Path = 'sound/' + cSound.file + '.mp3';
        this.load.audio(cSound.name, [oggPath, mp3Path]);
      }
    }

    for (var i = 0; i < this.gameConfig.backgrounds.length; i++) {
      this.load.image(
        this.gameConfig.backgrounds[i].name,
        this.gameConfig.backgrounds[i].file
      );
    }

    this.load.setPath('assets/spine');
    let atlasLength = this.spineData.length;
    for (let k = 0; k < atlasLength; k++) {
      /*  console.log(
        'loading ' + this.spineData[l].key + ' from ' + this.spineData[l].atlas
      ); */
      let key = this.spineData[k].key;
      let a = this.spineData[k].atlas;
      let data = this.spineData[k].data;

      var pixelRatio = this.scene.systems.game.device.os.pixelRatio;
      window.spriteScaler = 1;
      if (pixelRatio <= 1 && !this.scene.systems.game.device.os.desktop) {
        window.spriteScaler = 3;
        a = this.spineData[k].atlas_1x;
        key = this.spineData[k].key_1x;
        data = this.spineData[k].data_1x;
      }

      this.load.spine(
        key,
        data,
        a
      );
    }

    this.load.on('complete', this.loadComplete, this);
    this.load.start();
  }

  loadComplete() {
    this.scene.start('Title');
  }

  //  ------------------------------------------------------------------------

  /**
   *  Show the splash screen cover.
   *
   *  @private
   */
  showCover() {
    this.splash = this.add.image(0, 0, 'splash-screen').setOrigin(0);
    this.progressBG = this.add.image(568, 320, 'progress-bg').setOrigin(0.5);
  }

  /**
   *  Show the progress bar and set up its animation effect.
   *
   *  @private
   */
  showProgressBar() {
    //  Get the progress bar filler texture dimensions.
    const { width: w, height: h } = this.textures.get('progress-bar').get();

    //  Place the filler over the progress bar of the splash screen.
    const img = this.add
      .sprite(568 - w / 2, 320, 'progress-bar')
      .setOrigin(0, 0.5);

    //  Crop the filler along its width, proportional to the amount of files
    //  loaded.
    this.load.on('progress', v => img.setCrop(0, 0, Math.ceil(v * w), h));
  }

  createRotateMessage() {
    let isDesktop = this.scene.systems.game.device.os.desktop;
    if (!isDesktop) {
      this._errorMsgDiv = $("<div id='rotateMessage' />");
      $('body').append(this._errorMsgDiv);
      if (this.scene.systems.game.device.os.iPad) {
        this._errorMsgDiv.css({
          'background-image': "url('orientation/orientation_ipad.jpg" + "')",
        });
      }
      else {
        this._errorMsgDiv.css({
          'background-image': "url('orientation/orientation.jpg" + "')",
        });
      }
    }
  }

  showOrientationMessage() {
    let version = this.scene.systems.game.device.os.iOSVersion;
    if (version === 8) {
      this.hideNonFullScreenMessage();
    }
    $('#rotateMessage').addClass('showMessage');
    $('#rotateMessage').removeClass('hideMessage');

    for (let i = 0; i < this.game.scene.scenes.length; ++i) {
      this.scene.sleep(this.game.scene.scenes[i].scene.key);
    }
  }

  hideOrientationMessage() {
    $('#rotateMessage').addClass('hideMessage');
    $('#rotateMessage').removeClass('showMessage');
    // $(this.game.canvas).show();
    if (this.previousMuteState) {
      this.previousMuteState = false;
    }
    for (let i = 0; i < this.game.scene.scenes.length; ++i) {
      this.scene.wake(this.game.scene.scenes[i].scene.key);
    }

    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
  }

  //For iOS 8 - if the user touches the bottom 44px of the screen, it kicks them out of fullscreen mode.
  //SO! catch that case, and show a message to the user to swipe up to enter fullscreen again.
  gameResized() {
    if (!this.scene.systems.game.device.os.desktop) {
      if (this.scene.systems.game.device.os.iOS && this.scene.systems.game.device.os.iPhone) {
        if (this.scene.systems.game.device.os.iOSVersion <= 8) {
          if (this.scene.scene.scale.orientation === Phaser.Scale.LANDSCAPE) {
            if (this.lastHeight != window.innerHeight) {
              this.lastHeight = window.innerHeight;
              if (this.isMinimalView()) {
                // this.showNonFullScreenMessage();
              }
              else {
                this.scene.scene.scale.refresh();
              }
            }
            else {
              // this.hideNonFullScreenMessage();
            }
          }
        }
      }
    }
  }

  isMinimalView() {
    let spec = this.deviceSpec();
    if (spec !== undefined) {
      return (window.innerHeight !== spec[4]);
    }
    else {
      return false;
    }
  }

  showNonFullScreenMessage() {
    global.scrollTo(0, 0);

    $('#leftFullscreenMessage').removeClass('hideMessage');
    $('#fullscreenOverlay').removeClass('hideMessage');
    $('#leftFullscreenMessage').addClass('showMessage');
    $('#fullscreenOverlay').addClass('showMessage');

    this.nonFullScreenMessage.css({
      'width': global.innerWidth + 'px',
      'height': global.innerHeight * 2 + 'px',
    });
    this.nonFullScreenMessageBG.css({
      'width': global.innerWidth + 'px',
      'height': global.innerHeight + 'px',
    });
    for (let i = 0; i < this.game.scene.scenes.length; ++i) {
      this.scene.sleep(this.game.scene.scenes[i].scene.key);
    }
  }

  hideNonFullScreenMessage() {
    $('#leftFullscreenMessage').removeClass('showMessage');
    $('#fullscreenOverlay').removeClass('showMessage');
    $('#leftFullscreenMessage').addClass('hideMessage');
    $('#fullscreenOverlay').addClass('hideMessage');
    for (let i = 0; i < this.game.scene.scenes.length; ++i) {
      this.scene.wake(this.game.scene.scenes[i].scene.key);
    }
  }

  makeFullScreenMessage() {
    // var nonFullScreenMessage = $("<div id='leftFullscreenMessage'> <p>"+"Swipe Up"+"</p> <img src='orientation/ios8_swipeUp.png" + "' /> </div>");
    // $("body").append(nonFullScreenMessage);
    //
    // this.nonFullScreenMessage = nonFullScreenMessage;
  }

  makeFullScreenMessageBG() {
    var nonFullScreenMessageBG = $("<div id='fullscreenOverlay' />");
    $('body').append(nonFullScreenMessageBG);

    this.nonFullScreenMessageBG = nonFullScreenMessageBG;
  }

  deviceSpec() {
    let index, spec;

    const specs = [
      [1280, 1762, 1920, 1280, 320, 480, 2, 'iPhone 4'],
      [1280, 2114, 2272, 1280, 320, 568, 2, 'iPhone 5 or 5s'],

      [1500, 2510, 2668, 1500, 375, 667, 2, 'iPhone 6'],
      // Equivalent to iPhone 5
      // [1280, 2114, 2272, 1280, 320, 568, 2, 'iPhone 6 (Zoomed)'],

      [1656, 2785, 2944, 1656, 414, 736, 3, 'iPhone 6 plus'],
      [1500, 2509, 2668, 1500, 375, 667, 3, 'iPhone 6 plus (Zoomed)'],

      [3072, 3936, 4096, 2912, 768, 1024, 1, 'iPad 2'],
      [3072, 3938, 4096, 2914, 768, 1024, 2, 'iPad Air or Retina'],

      [4096, 5306, 5464, 3938, 1024, 1366, 2, 'iPad Pro']
    ];

    index = specs.length;
    while (index--) {
      if (window.screen.width === specs[index][4] &&
        window.screen.height === specs[index][5] &&
        window.devicePixelRatio === specs[index][6]) {
        spec = specs[index];

        break;
      }
    }

    return spec;
  }

  destroy() {
    this.gameConfig.destroy();
    this.spineData.destroy();
    this.sounds.destroy();
    this.helpConfig.destroy();
    this.splash.destroy();
    this.progressBG.destroy();
    super.destroy();
  }
}
